import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listCrawlsPage,
  type CrawlListFilters,
  type CrawlListPage,
  type CrawlSummary,
} from "../lib/api";

/**
 * Runs history — paginated, filterable table of every crawl the CLI or
 * scheduler has produced. Consumes GET /api/crawls with the new pagination
 * envelope (Phase 0 #98). The Dashboard at "/" stays as a card grid for
 * the most recent crawls; this is the dense, spreadsheet-y view for when
 * you have 30+ runs.
 *
 * Filters stay in component state (not the URL) for v0 — the cost/benefit
 * of plumbing query-string sync isn't worth it until someone asks to share
 * a filtered link.
 */
const PAGE_SIZE = 25;

type FrameworkFilter = "all" | "java" | "python" | "unknown";
type AuthedFilter = "any" | "authed" | "anon";

export function RunsPage(): JSX.Element {
  const [page, setPage] = useState<CrawlListPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [offset, setOffset] = useState(0);
  const [framework, setFramework] = useState<FrameworkFilter>("all");
  const [authed, setAuthed] = useState<AuthedFilter>("any");
  const [q, setQ] = useState("");

  // Debounce the text filter so every keystroke doesn't fire a request.
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 0 whenever the filter changes — paging past the new
  // total is a UX dead end.
  useEffect(() => {
    setOffset(0);
  }, [framework, authed, debouncedQ]);

  const fetchPage = useCallback(async () => {
    setBusy(true);
    setError(null);
    const filters: CrawlListFilters = { limit: PAGE_SIZE, offset };
    if (framework !== "all") filters.framework = framework;
    if (authed !== "any") filters.authed = authed === "authed";
    if (debouncedQ.trim()) filters.q = debouncedQ.trim();
    try {
      const data = await listCrawlsPage(filters);
      setPage(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [offset, framework, authed, debouncedQ]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const total = page?.total ?? 0;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Runs</h1>
          <p className="mt-1 text-sm text-slate-400">
            Every crawl this workspace has produced, paginated. Filter by
            framework, whether auth was injected, or a substring of the URL or
            crawl id.
          </p>
        </div>
        <div className="text-xs text-slate-500 font-mono">api: /api/crawls</div>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <SegGroup<FrameworkFilter>
          label="Framework"
          value={framework}
          onChange={setFramework}
          options={[
            { v: "all", label: "All" },
            { v: "java", label: "Java" },
            { v: "python", label: "Python" },
            { v: "unknown", label: "Unknown" },
          ]}
        />
        <SegGroup<AuthedFilter>
          label="Auth"
          value={authed}
          onChange={setAuthed}
          options={[
            { v: "any", label: "Any" },
            { v: "authed", label: "Authed" },
            { v: "anon", label: "Anon" },
          ]}
        />
        <label className="ml-auto block text-xs text-slate-400">
          <span className="mr-2 uppercase tracking-wider">Search</span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="URL or id…"
            className="w-56 rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100"
          />
        </label>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-rose-700 bg-rose-900/40 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {page === null && !error && <LoadingBanner />}

      {page !== null && page.crawls.length === 0 && !error && (
        <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center">
          <p className="text-slate-300">
            {hasAnyFilter(framework, authed, debouncedQ)
              ? "No runs match these filters."
              : "No runs yet."}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {hasAnyFilter(framework, authed, debouncedQ)
              ? "Clear or relax the filters to see more."
              : "Kick off a crawl from the New Crawl tab or a schedule."}
          </p>
        </div>
      )}

      {page !== null && page.crawls.length > 0 && (
        <>
          <div className="overflow-hidden rounded-lg border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Run</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3">Framework</th>
                  <th className="px-4 py-3">Pages</th>
                  <th className="px-4 py-3">Elements</th>
                  <th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-950">
                {page.crawls.map((c) => (
                  <RunRow key={c.id} crawl={c} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
            <div>
              {pageStart === 0
                ? "No results"
                : `${pageStart}–${pageEnd} of ${total}`}
              {busy && <span className="ml-2 text-slate-500">updating…</span>}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0 || busy}
                className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1 text-sm text-slate-300 hover:border-slate-600 disabled:opacity-40"
              >
                ← Prev
              </button>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={!page.hasMore || busy}
                className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1 text-sm text-slate-300 hover:border-slate-600 disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function hasAnyFilter(
  framework: FrameworkFilter,
  authed: AuthedFilter,
  q: string,
): boolean {
  return framework !== "all" || authed !== "any" || q.trim().length > 0;
}

function RunRow({ crawl }: { crawl: CrawlSummary }): JSX.Element {
  return (
    <tr className="text-slate-200">
      <td className="px-4 py-3">
        <Link
          to={`/crawls/${encodeURIComponent(crawl.id)}`}
          className="font-mono text-xs text-indigo-300 hover:text-indigo-200"
        >
          {crawl.id}
        </Link>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-400">
        {shortenUrl(crawl.targetUrl)}
      </td>
      <td className="px-4 py-3">
        <FrameworkBadge framework={crawl.framework} authed={crawl.authed} />
      </td>
      <td className="px-4 py-3 font-mono text-xs">{crawl.pageCount}</td>
      <td className="px-4 py-3 font-mono text-xs">{crawl.elementCount}</td>
      <td className="px-4 py-3 font-mono text-xs text-slate-400">
        {formatDuration(crawl.durationMs)}
      </td>
      <td className="px-4 py-3 text-xs text-slate-400">{formatDate(crawl.crawledAt)}</td>
      <td className="px-4 py-3 text-right">
        <Link
          to={`/crawls/${encodeURIComponent(crawl.id)}`}
          className="mr-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:border-slate-600"
        >
          Open
        </Link>
        <Link
          to={`/crawls/${encodeURIComponent(crawl.id)}/tests`}
          className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:border-slate-600"
        >
          Tests
        </Link>
      </td>
    </tr>
  );
}

function FrameworkBadge({
  framework,
  authed,
}: {
  framework: CrawlSummary["framework"];
  authed: boolean;
}): JSX.Element {
  const tone =
    framework === "java"
      ? "bg-orange-500/15 text-orange-300 border-orange-500/30"
      : framework === "python"
        ? "bg-sky-500/15 text-sky-300 border-sky-500/30"
        : "bg-slate-700/30 text-slate-400 border-slate-600/40";
  return (
    <div className="flex items-center gap-1">
      <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded border ${tone}`}>
        {framework}
      </span>
      {authed && (
        <span
          className="text-[10px] uppercase tracking-wide px-2 py-1 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
          title="auth injected"
        >
          auth
        </span>
      )}
    </div>
  );
}

function SegGroup<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { v: T; label: string }[];
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <div className="inline-flex rounded-md border border-slate-700 bg-slate-950 p-0.5">
        {options.map((o) => {
          const active = o.v === value;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onChange(o.v)}
              className={
                "rounded px-2 py-1 text-xs transition " +
                (active
                  ? "bg-slate-800 text-slate-100"
                  : "text-slate-400 hover:text-slate-200")
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LoadingBanner(): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
      Loading runs…
    </div>
  );
}

function shortenUrl(url: string): string {
  try {
    const u = new URL(url);
    const tail = u.pathname === "/" ? "" : u.pathname;
    return u.host + tail;
  } catch {
    return url;
  }
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m${s}s`;
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}
